"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle2 } from "lucide-react";
import { useActionState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { type PartnerInquiryState, submitPartnerInquiry } from "../_lib/actions";
const initialState: PartnerInquiryState = {
  status: "idle"
};

/** Red asterisk marking a required field — decorative; `required` on the
 *  input is what screen readers announce. */
function RequiredMark() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span aria-hidden="true" className="-ml-1 text-(--red)">*</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Per-field validation message wired to the input via aria-describedby. */
function FieldError(t0) {
  const $ = _c(3);
  const {
    id,
    message
  } = t0;
  if (!message) {
    return null;
  }
  let t1;
  if ($[0] !== id || $[1] !== message) {
    t1 = <p id={id} role="alert" className="text-[12px] font-medium text-(--red)">{message}</p>;
    $[0] = id;
    $[1] = message;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function PartnerContactForm() {
  const $ = _c(68);
  const [state, formAction, isPending] = useActionState(submitPartnerInquiry, initialState);
  if (state.status === "success") {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <div role="status" className="mx-auto w-full max-w-[480px] rounded-(--r-lg) border border-(--color-divider) bg-surface-2 p-8 text-center"><CheckCircle2 size={32} className="mx-auto text-(--green)" /><p className="type-h3 mt-3 text-body">Thank you!</p><p className="mt-2 text-[14px] leading-[1.65] text-muted">We received your inquiry and will get back to you within 3 business days.</p></div>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Label htmlFor="partner-firstname">First name<RequiredMark /></Label>;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = state.values?.firstname;
  const t2 = state.fieldErrors?.firstname ? true : undefined;
  const t3 = state.fieldErrors?.firstname ? "partner-firstname-error" : undefined;
  let t4;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== t3) {
    t4 = <Input id="partner-firstname" name="firstname" autoComplete="given-name" required={true} maxLength={100} defaultValue={t1} aria-invalid={t2} aria-describedby={t3} />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = state.fieldErrors?.firstname;
  let t6;
  if ($[6] !== t5) {
    t6 = <FieldError id="partner-firstname-error" message={t5} />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = <div className="flex flex-col gap-1.5">{t0}{t4}{t6}</div>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Label htmlFor="partner-lastname">Last name<RequiredMark /></Label>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = state.values?.lastname;
  const t10 = state.fieldErrors?.lastname ? true : undefined;
  const t11 = state.fieldErrors?.lastname ? "partner-lastname-error" : undefined;
  let t12;
  if ($[12] !== t10 || $[13] !== t11 || $[14] !== t9) {
    t12 = <Input id="partner-lastname" name="lastname" autoComplete="family-name" required={true} maxLength={100} defaultValue={t9} aria-invalid={t10} aria-describedby={t11} />;
    $[12] = t10;
    $[13] = t11;
    $[14] = t9;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = state.fieldErrors?.lastname;
  let t14;
  if ($[16] !== t13) {
    t14 = <FieldError id="partner-lastname-error" message={t13} />;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t12 || $[19] !== t14) {
    t15 = <div className="flex flex-col gap-1.5">{t8}{t12}{t14}</div>;
    $[18] = t12;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== t15 || $[22] !== t7) {
    t16 = <div className="grid gap-4 sm:grid-cols-2">{t7}{t15}</div>;
    $[21] = t15;
    $[22] = t7;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Label htmlFor="partner-email">Email<RequiredMark /></Label>;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const t18 = state.values?.email;
  const t19 = state.fieldErrors?.email ? true : undefined;
  const t20 = state.fieldErrors?.email ? "partner-email-error" : undefined;
  let t21;
  if ($[25] !== t18 || $[26] !== t19 || $[27] !== t20) {
    t21 = <Input id="partner-email" name="email" type="email" autoComplete="email" required={true} maxLength={254} defaultValue={t18} aria-invalid={t19} aria-describedby={t20} />;
    $[25] = t18;
    $[26] = t19;
    $[27] = t20;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  const t22 = state.fieldErrors?.email;
  let t23;
  if ($[29] !== t22) {
    t23 = <FieldError id="partner-email-error" message={t22} />;
    $[29] = t22;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== t21 || $[32] !== t23) {
    t24 = <div className="flex flex-col gap-1.5">{t17}{t21}{t23}</div>;
    $[31] = t21;
    $[32] = t23;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Label htmlFor="partner-company">Company<RequiredMark /></Label>;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  const t26 = state.values?.company;
  const t27 = state.fieldErrors?.company ? true : undefined;
  const t28 = state.fieldErrors?.company ? "partner-company-error" : undefined;
  let t29;
  if ($[35] !== t26 || $[36] !== t27 || $[37] !== t28) {
    t29 = <Input id="partner-company" name="company" autoComplete="organization" required={true} maxLength={200} defaultValue={t26} aria-invalid={t27} aria-describedby={t28} />;
    $[35] = t26;
    $[36] = t27;
    $[37] = t28;
    $[38] = t29;
  } else {
    t29 = $[38];
  }
  const t30 = state.fieldErrors?.company;
  let t31;
  if ($[39] !== t30) {
    t31 = <FieldError id="partner-company-error" message={t30} />;
    $[39] = t30;
    $[40] = t31;
  } else {
    t31 = $[40];
  }
  let t32;
  if ($[41] !== t29 || $[42] !== t31) {
    t32 = <div className="flex flex-col gap-1.5">{t25}{t29}{t31}</div>;
    $[41] = t29;
    $[42] = t31;
    $[43] = t32;
  } else {
    t32 = $[43];
  }
  let t33;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Label htmlFor="partner-message">Message</Label>;
    $[44] = t33;
  } else {
    t33 = $[44];
  }
  const t34 = state.values?.message;
  const t35 = state.fieldErrors?.message ? true : undefined;
  const t36 = state.fieldErrors?.message ? "partner-message-error" : undefined;
  let t37;
  if ($[45] !== t34 || $[46] !== t35 || $[47] !== t36) {
    t37 = <textarea id="partner-message" name="message" rows={4} maxLength={2000} defaultValue={t34} placeholder={"Tell us about your business and its parking needs\u2026"} aria-invalid={t35} aria-describedby={t36} className="flex w-full resize-y rounded-(--r-lg) border border-(--ink-20) bg-(--ink-05) px-4 py-3 font-ui text-[14px] font-medium text-(--ink) outline-none [transition:border-color_var(--dur-fast)_var(--ease-snap),background-color_var(--dur-fast)_var(--ease-snap),box-shadow_var(--dur-fast)_var(--ease-snap)] placeholder:font-normal placeholder:text-(--ink-40) focus:border-(--blue) focus:bg-white focus:shadow-[0_0_0_3px_rgba(0,115,233,0.12)] aria-invalid:border-(--red) aria-invalid:bg-white aria-invalid:shadow-[0_0_0_3px_rgba(255,59,48,0.10)] disabled:pointer-events-none disabled:opacity-50" />;
    $[45] = t34;
    $[46] = t35;
    $[47] = t36;
    $[48] = t37;
  } else {
    t37 = $[48];
  }
  const t38 = state.fieldErrors?.message;
  let t39;
  if ($[49] !== t38) {
    t39 = <FieldError id="partner-message-error" message={t38} />;
    $[49] = t38;
    $[50] = t39;
  } else {
    t39 = $[50];
  }
  let t40;
  if ($[51] !== t37 || $[52] !== t39) {
    t40 = <div className="flex flex-col gap-1.5">{t33}{t37}{t39}</div>;
    $[51] = t37;
    $[52] = t39;
    $[53] = t40;
  } else {
    t40 = $[53];
  }
  let t41;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden"><label htmlFor="partner-website">Website</label><input id="partner-website" name="website" type="text" tabIndex={-1} autoComplete="off" /></div>;
    $[54] = t41;
  } else {
    t41 = $[54];
  }
  const t42 = isPending ? "Sending\u2026" : "Send inquiry";
  let t43;
  if ($[55] !== isPending || $[56] !== t42) {
    t43 = <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isPending}>{t42}</Button>;
    $[55] = isPending;
    $[56] = t42;
    $[57] = t43;
  } else {
    t43 = $[57];
  }
  let t44;
  if ($[58] !== state.error || $[59] !== state.status) {
    t44 = state.status === "error" && <p role="alert" className="text-center text-[13px] text-(--red)">{state.error}</p>;
    $[58] = state.error;
    $[59] = state.status;
    $[60] = t44;
  } else {
    t44 = $[60];
  }
  let t45;
  if ($[61] !== t16 || $[62] !== t24 || $[63] !== t32 || $[64] !== t40 || $[65] !== t43 || $[66] !== t44) {
    t45 = <form action={formAction} className="mx-auto flex w-full max-w-[480px] flex-col gap-4 text-left">{t16}{t24}{t32}{t40}{t41}{t43}{t44}</form>;
    $[61] = t16;
    $[62] = t24;
    $[63] = t32;
    $[64] = t40;
    $[65] = t43;
    $[66] = t44;
    $[67] = t45;
  } else {
    t45 = $[67];
  }
  return t45;
}